import { useEffect } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { subscribeIdToken } from '@/lib/firebase'
import { useAuthStore } from '@/stores/auth-store'
import { apiFetch } from '@/lib/api'
import type { VibeUser } from '@vibe/shared-types'

/**
 * 앱 부팅 시 Firebase Auth 상태를 구독해 auth-store에 동기화하고,
 * 로그인 직후 백엔드 `/api/auth/me` 호출로 VibeUser 프로필을 채운다.
 *
 * 보호 라우트 진입 차단은 "Firebase user 존재 여부" 단일 기준.
 * `/api/auth/me` 일시 실패에도 사용자는 계속 진행할 수 있어야 함 (백엔드 hiccup 으로 로그인 흐름 끊김 방지).
 */
const PUBLIC_ROUTES = ['/', '/auth', '/payment', '/archive']

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.includes(pathname)
}

export function AuthBootstrap() {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { setFirebaseUser, setIdToken, setProfile, setStatus, firebaseUser, status } =
    useAuthStore()

  useEffect(() => {
    let cancelled = false
    const unsub = subscribeIdToken(async (user) => {
      if (cancelled) return
      setFirebaseUser(user)
      if (!user) {
        setIdToken(null)
        setProfile(null)
        setStatus('signedOut')
        return
      }
      setStatus('signingIn')
      try {
        const token = await user.getIdToken()
        setIdToken(token)
      } catch (err) {
        console.error('AuthBootstrap.id_token_failed', err)
      }

      try {
        const profile = await apiFetch<VibeUser>('/api/auth/me')
        if (cancelled) return
        setProfile(profile)
      } catch (err) {
        if (cancelled) return
        console.warn('AuthBootstrap.me_failed (will retry on next route load)', err)
      } finally {
        if (!cancelled) setStatus('ready')
      }
    })
    return () => {
      cancelled = true
      unsub()
    }
  }, [setFirebaseUser, setIdToken, setProfile, setStatus])

  // Firebase user 존재 여부 + 라우트 공개 여부로만 redirect 판단.
  // status state는 race 가능해서 firebaseUser를 진실 공급원으로.
  useEffect(() => {
    if (status === 'loading') return // 첫 구독 콜백 전엔 판단 보류
    if (!firebaseUser && !isPublicRoute(pathname)) {
      navigate({ to: '/auth' })
    }
  }, [firebaseUser, status, pathname, navigate])

  return null
}
